import { HubConnectionState } from '@microsoft/signalr';
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import { createSquareMarketplaceLogos } from 'src/components/common/MarketplaceLogo';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useBulkEditHubContext } from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { AlertWithSuppressionDialog } from 'src/dialogs/AlertWithSuppressionDialog';
import { BulkEditFooter } from 'src/dialogs/BulkEdits/common/BulkEditFooter';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { usePriceChangeDialogs } from 'src/hooks/usePriceChangeDialogs';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { DetailGroup } from 'src/modals/common';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getListingDetailsUpdateInput,
  isListingDetailsInputChanged,
} from 'src/utils/inventoryUtils';
import { roundToPrecision } from 'src/utils/numberFormatter';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  Event,
  ListingDetails,
  ListingDetailsUpdateInput,
  Marketplace,
  MarketplaceAccountSetting,
  Permission,
} from 'src/WebApiController';

import {
  MarketplacePriceInputHeader,
  MarketplacePriceInputTile,
  StubHubManualPrice,
} from './BulkEditMarketplacePriceMobileContent.components';

export type BulkEditMarketplacePricesMobileContentProps = {
  updateKey: string;
  event?: Event;
  onOkay: (
    listing: ListingDetailsUpdateInput | null,
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreviewWithDetails
  ) => void;
  onCancel: () => void;
};

export const BulkEditMarketplacePricesMobileContent = ({
  ...rest
}: BulkEditMarketplacePricesMobileContentProps) => {
  const { loginContext } = useAppContext();
  const methods = useForm<ListingDetailsUpdateInput>({
    defaultValues: getListingDetailsUpdateInput(
      null,
      null,
      loginContext?.user?.activeAccount?.marketplaceSettings
    ),
  });

  return (
    <FormProvider {...methods}>
      <BulkEditMarketplacePricesMobileContentDetails {...rest} {...methods} />
    </FormProvider>
  );
};

function BulkEditMarketplacePricesMobileContentDetails({
  updateKey,
  event,
  onOkay,
  onCancel,
  handleSubmit,
  watch,
  reset,
  formState,
}: BulkEditMarketplacePricesMobileContentProps &
  Omit<
    ComponentProps<typeof FormProvider<ListingDetailsUpdateInput, unknown>>,
    'children'
  >) {
  const { loginContext } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();
  const [isLoading, setIsLoading] = useState(false);
  const input = watch();

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);

        await refreshExpandedListItems();
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }
        setIsRefreshing(false);
        // progress kept to allow the BulkEditStatusPopover to show the result
      }
      onCancel();
    },
    [
      onCancel,
      refreshExpandedListItems,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        onClose(doneProgress, true);
      }
    },
    [onClose]
  );

  const { bulkEditHub, progress, preview, initJob } = useBulkEditHub(
    ActionOutboxEntityType.Listing,
    BulkActionType.UpdateMarketplacePrices,
    updateKey,
    onBulkEditDone
  );

  useEffect(() => {
    reset(
      getListingDetailsUpdateInput(
        null,
        null,
        loginContext?.user?.activeAccount?.marketplaceSettings
      )
    );
    initJob();
    return () => {
      setMainDialogOpened(false);
      if (progress) {
        setSelectionMode(undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  const [isRefreshing, setIsRefreshing] = useState(false);

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        onOkay(null, false, onPreviewReceived);
      } else {
        handleSubmit((i) =>
          onOkay(i, hasBackgroundBulkEditFeature, undefined, preview)
        )();
      }
      setIsLoading(false);
    },
    [onOkay, handleSubmit, hasBackgroundBulkEditFeature, preview]
  );

  const isInputValid = useMemo(() => {
    return isListingDetailsInputChanged(
      input,
      formState.defaultValues as ListingDetailsUpdateInput
    );
  }, [input, formState.defaultValues]);

  const submittButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Stack direction="column" width="full" height="full">
      <div style={{ flex: 1, minHeight: 0 }}>
        <BulkEditStatus
          entityType={ActionOutboxEntityType.Listing}
          isLoading={isRefreshing || isLoading}
          updateKey={updateKey}
        >
          <MarketplacePricesForm event={event} />
        </BulkEditStatus>
      </div>

      <Stack justifyContent="spaceBetween" width="full">
        <BulkEditFooter
          entityType={ActionOutboxEntityType.Listing}
          isLoading={isLoading}
          hasBackgroundBulkEditFeature={hasBackgroundBulkEditFeature}
          onClose={onClose}
          onSubmit={onSubmitHandler}
          disabled={!isInputValid}
          submittButtonRef={submittButtonRef}
        />
      </Stack>
    </Stack>
  );
}

const MarketplacePricesForm = ({ event }: { event: Event | undefined }) => {
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();
  const uiCurrency = useMemo(
    () =>
      getUiCurrency(loginContext?.user?.activeAccount?.currencyCode ?? 'USD'),
    [getUiCurrency, loginContext?.user?.activeAccount?.currencyCode]
  );
  const marketplaceAccountSetting = useMemo(() => {
    return (
      loginContext?.user?.activeAccount?.marketplaceSettings ?? []
    ).reduce(
      (res, mp) => {
        res[mp.mkp] = mp;
        return res;
      },
      {} as Record<Marketplace, MarketplaceAccountSetting>
    );
  }, [loginContext?.user?.activeAccount?.marketplaceSettings]);

  const canChangePricer = useUserHasAnyOfPermissions(
    Permission.Inventory_SetPrice
  );

  const { watch, setValue } = useFormContext<ListingDetailsUpdateInput>();
  const listPrice = watch('listPrice');
  const marketplacePriceUpdates = watch('marketplacePriceUpdates');

  const logos = createSquareMarketplaceLogos(false);

  const { onPriceChanged, warningDialog, alerts } = usePriceChangeDialogs(
    event?.virtId,
    undefined,
    true
  );

  const onPriceEdit = useCallback(
    (newPrice: number, priceByMarketplaceEnabled: boolean, idx: number) => {
      if (listPrice) {
        const newMarkup = (newPrice - listPrice) / listPrice;

        if (priceByMarketplaceEnabled) {
          setValue(
            `marketplacePriceUpdates.${idx}.markup`,
            roundToPrecision(newMarkup * 100, 2)
          );
          onPriceChanged();
        }
      }
    },
    [listPrice, onPriceChanged, setValue]
  );

  return (
    <Stack
      direction="column"
      gap="xxl"
      style={{ padding: vars.spacing.xs, color: vars.color.textPrimaryV2 }}
    >
      {(listPrice != null ||
        marketplacePriceUpdates.some((m) => m.listPrice != null)) && (
        <DetailGroup style={{ gridColumn: '1 / span 3' }}>
          <WarningMessage
            message={
              <Content
                id={ContentId.BulkEditListPriceAdjustingFloorCeilingWarning}
              />
            }
          />
        </DetailGroup>
      )}
      <StubHubManualPrice
        disabled={!canChangePricer}
        currency={uiCurrency}
        MarketplaceLog={logos[Marketplace.StubHub]}
        marketplaceAccountSettings={
          marketplaceAccountSetting[Marketplace.StubHub]
        }
        onPriceChanged={onPriceChanged}
      />
      <Stack width="full" direction="column" gap="l">
        <MarketplacePriceInputHeader />
        <Stack width="full" direction="column" gap="l">
          {marketplacePriceUpdates.map((mp, index) => {
            const marketplaceLogo = logos[mp.marketplace];
            return (
              <MarketplacePriceInputTile
                key={mp.marketplace}
                inputIndex={index}
                currency={uiCurrency}
                MarketplaceLog={marketplaceLogo}
                marketplaceAccountSettings={
                  marketplaceAccountSetting[mp.marketplace]
                }
                disabled={!canChangePricer}
                onPriceEdit={onPriceEdit}
              />
            );
          })}
        </Stack>
      </Stack>
      <AlertWithSuppressionDialog
        headerText={<Content id={ContentId.ManualPriceWarning} />}
        size="md"
        {...warningDialog.dialogProps}
        alerts={alerts}
        viagogoVirtualId={event?.virtId}
        onOkay={() => warningDialog.closeDialog()}
        onCancel={() => warningDialog.closeDialog()}
      />
    </Stack>
  );
};
